<template>
  <div class="container-fluid py-4">
    <div v-if="isLoading" class="text-center my-5"><div class="spinner-border text-primary"></div></div>

    <template v-else-if="b">
      <!-- En-tête -->
      <div class="d-flex justify-content-between align-items-start mb-4 header-clinique flex-wrap gap-2">
        <div>
          <router-link to="/creances-assurance" class="small text-decoration-none">
            <i class="bi bi-arrow-left"></i> Créances assurances
          </router-link>
          <h2 class="fw-bold mb-0 mt-1">Bordereau {{ b.numero }}</h2>
          <div class="text-muted">
            {{ b.assurance?.nom }} · du {{ formatDate(b.periodeDebut) }} au {{ formatDate(b.periodeFin) }}
            · <span class="badge" :class="badgeBordereau(b.statut)">{{ statutsBordereau[b.statut] }}</span>
          </div>
        </div>
        <div class="d-flex flex-wrap gap-2">
          <button class="btn btn-outline-secondary" @click="pdf"><i class="bi bi-printer me-1"></i> Imprimer (PDF)</button>
          <button class="btn btn-outline-secondary" @click="csv"><i class="bi bi-filetype-csv me-1"></i> Excel (CSV)</button>
          <template v-if="peutGerer">
            <button v-if="b.statut === 'BROUILLON'" class="btn btn-outline-danger" @click="supprimer">Supprimer</button>
            <button v-if="b.statut === 'BROUILLON'" class="btn btn-primary" @click="envoyer">
              <i class="bi bi-send me-1"></i> Marquer comme envoyé
            </button>
            <button v-if="['ENVOYE', 'PARTIELLEMENT_REGLE'].includes(b.statut)" class="btn btn-success" @click="ouvrirReglement">
              <i class="bi bi-cash-coin me-1"></i> Enregistrer un règlement
            </button>
          </template>
        </div>
      </div>

      <!-- Totaux -->
      <div class="row g-3 mb-4">
        <div class="col-6 col-md-3"><div class="card card-clinique p-3"><small class="text-muted">Actes</small><div class="fs-4 fw-bold">{{ b.nombreCreances }}</div></div></div>
        <div class="col-6 col-md-3"><div class="card card-clinique p-3"><small class="text-muted">Total demandé</small><div class="fs-4 fw-bold">{{ fcfa(b.montantTotal) }}</div></div></div>
        <div class="col-6 col-md-3"><div class="card card-clinique p-3"><small class="text-muted">Réglé</small><div class="fs-4 fw-bold text-success">{{ fcfa(b.montantRegle) }}</div></div></div>
        <div class="col-6 col-md-3"><div class="card card-clinique p-3"><small class="text-muted">Reste dû</small><div class="fs-4 fw-bold" :class="b.reste > 0 ? 'text-danger' : ''">{{ fcfa(b.reste) }}</div></div></div>
      </div>

      <!-- Actes -->
      <div class="card shadow-sm card-clinique mb-4">
        <div class="table-responsive">
          <table class="table table-sm align-middle mb-0 modern-table-clinique">
            <thead>
              <tr>
                <th>#</th><th>Date</th><th>Patient</th><th>N° assuré</th><th>Nature</th>
                <th class="text-end">Montant</th><th class="text-end">Réglé</th><th>Statut</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(c, i) in b.creances" :key="c.id">
                <td>{{ i + 1 }}</td>
                <td class="text-nowrap">{{ formatDate(c.dateActe) }}</td>
                <td>{{ c.patient ? `${c.patient.nom} ${c.patient.prenom}` : '' }}<div class="small text-muted">{{ c.patient?.numeroDossier }}</div></td>
                <td>{{ c.numeroAssure }}</td>
                <td class="small">{{ libelleOrigine(c.origine) }}<div class="text-muted">{{ c.reference }}</div></td>
                <td class="text-end">{{ fcfa(c.montant) }}</td>
                <td class="text-end">{{ fcfa(c.montantRegle) }}</td>
                <td>
                  <span class="badge" :class="badge(c.statut)">{{ statuts[c.statut] || c.statut }}</span>
                  <div v-if="c.motifRejet" class="small text-danger">{{ c.motifRejet }}</div>
                </td>
                <td>
                  <button
                    v-if="peutGerer && ['ENVOYEE', 'PARTIELLEMENT_REGLEE'].includes(c.statut)"
                    class="btn btn-sm btn-outline-danger"
                    @click="rejeter(c)"
                  >Rejet</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Règlements reçus -->
      <div class="card card-clinique p-3" v-if="b.reglements?.length">
        <h6 class="fw-bold">Règlements reçus</h6>
        <table class="table table-sm mb-0">
          <thead><tr><th>Date</th><th>Moyen</th><th>Référence</th><th class="text-end">Montant</th><th>Saisi par</th></tr></thead>
          <tbody>
            <tr v-for="r in b.reglements" :key="r.id">
              <td>{{ formatDate(r.dateReglement) }}</td>
              <td>{{ moyens[r.moyen] || r.moyen }}</td>
              <td>{{ r.reference }}</td>
              <td class="text-end">{{ fcfa(r.montant) }}</td>
              <td>{{ r.createdBy }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Fenêtre règlement -->
    <div v-if="reglement" class="modal-backdrop-custom" @click.self="reglement = null">
      <div class="modal-card card shadow">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Règlement de {{ b.assurance?.nom }}</h5>
          <button class="btn-close" @click="reglement = null"></button>
        </div>
        <div class="card-body">
          <p class="small text-muted mb-3">
            Reste dû : <strong>{{ fcfa(b.reste) }}</strong>. Le montant est affecté aux actes les plus anciens.
          </p>
          <div class="row g-2">
            <div class="col-md-6">
              <label class="form-label">Montant reçu (FCFA)</label>
              <input v-model.number="reglement.montant" type="number" min="1" :max="b.reste" class="form-control">
            </div>
            <div class="col-md-6">
              <label class="form-label">Date</label>
              <input v-model="reglement.date" type="date" class="form-control">
            </div>
            <div class="col-md-6">
              <label class="form-label">Moyen</label>
              <select v-model="reglement.moyen" class="form-select">
                <option v-for="(l, m) in moyens" :key="m" :value="m">{{ l }}</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label">Référence (n° virement, chèque…)</label>
              <input v-model="reglement.reference" class="form-control">
            </div>
          </div>
          <div v-if="erreur" class="alert alert-danger mt-3 mb-0 py-2">{{ erreur }}</div>
        </div>
        <div class="card-footer text-end">
          <button class="btn btn-secondary me-2" @click="reglement = null">Annuler</button>
          <button class="btn btn-success" :disabled="enCours || !reglement.montant" @click="enregistrerReglement">Enregistrer</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api, { messageErreur, notifier } from '@/api'
import dayjs from 'dayjs'

export default {
  name: 'BordereauAssurance',
  props: { id: { type: [String, Number], required: true } },
  data() {
    return {
      b: null,
      isLoading: false,
      reglement: null,
      erreur: '',
      enCours: false,
      statuts: {
        A_ENVOYER: 'À envoyer', ENVOYEE: 'Envoyée', PARTIELLEMENT_REGLEE: 'Partiellement réglée',
        REGLEE: 'Réglée', REJETEE: 'Rejetée', ANNULEE: 'Annulée'
      },
      statutsBordereau: { BROUILLON: 'Brouillon', ENVOYE: 'Envoyé', PARTIELLEMENT_REGLE: 'Partiellement réglé', REGLE: 'Réglé' },
      moyens: { VIREMENT: 'Virement', CHEQUE: 'Chèque', ESPECES: 'Espèces', MOBILE_MONEY: 'Mobile Money' }
    }
  },
  computed: {
    peutGerer() {
      return this.$store.getters.hasPermission('ASSURANCE_CREANCES_GERER')
    }
  },
  created() {
    this.charger()
  },
  methods: {
    async charger() {
      this.isLoading = !this.b
      try {
        this.b = (await api.getItem('bordereaux-assurance', this.id)).data
      } catch (e) {
        notifier(messageErreur(e, 'Bordereau introuvable.'))
        this.$router.push('/creances-assurance')
      } finally {
        this.isLoading = false
      }
    },
    async envoyer() {
      if (!confirm("Confirmer l'envoi de ce bordereau à l'assureur ? Il ne pourra plus être modifié.")) return
      try {
        this.b = { ...this.b, ...(await api.post(`bordereaux-assurance/${this.id}/envoyer`, {})).data }
        await this.charger()
        notifier('Bordereau marqué comme envoyé.', 'success')
      } catch (e) {
        notifier(messageErreur(e, "L'envoi n'a pas pu être enregistré."))
      }
    },
    async supprimer() {
      if (!confirm('Supprimer ce brouillon ? Ses actes redeviendront « à envoyer ».')) return
      try {
        await api.deleteItem('bordereaux-assurance', this.id)
        this.$router.push('/creances-assurance')
      } catch (e) {
        notifier(messageErreur(e, 'La suppression a échoué.'))
      }
    },
    ouvrirReglement() {
      this.erreur = ''
      this.reglement = { montant: this.b.reste, date: dayjs().format('YYYY-MM-DD'), moyen: 'VIREMENT', reference: '' }
    },
    async enregistrerReglement() {
      this.erreur = ''
      this.enCours = true
      try {
        await api.post(`bordereaux-assurance/${this.id}/reglements`, this.reglement)
        this.reglement = null
        await this.charger()
        notifier('Règlement enregistré.', 'success')
      } catch (e) {
        this.erreur = messageErreur(e, "Le règlement n'a pas pu être enregistré.")
      } finally {
        this.enCours = false
      }
    },
    async rejeter(c) {
      const motif = prompt('Motif du rejet (obligatoire) :')
      if (motif === null) return
      if (!motif.trim()) return alert('Le motif est obligatoire.')
      try {
        await api.post(`creances-assurance/${c.id}/rejeter`, { motif: motif.trim() })
        await this.charger()
      } catch (e) {
        notifier(messageErreur(e, "Le rejet n'a pas pu être enregistré."))
      }
    },
    async pdf() {
      try {
        await api.telecharger(`bordereaux-assurance/${this.id}/pdf`, `${this.b.numero}.pdf`, true)
      } catch (e) {
        notifier('Le PDF n’a pas pu être généré.')
      }
    },
    async csv() {
      try {
        await api.telecharger(`bordereaux-assurance/${this.id}/csv`, `${this.b.numero}.csv`)
      } catch (e) {
        notifier('L’export n’a pas pu être généré.')
      }
    },
    libelleOrigine(o) {
      return { CONSULTATION: 'Consultation', FACTURE: 'Services', HOSPITALISATION: 'Hospitalisation', VENTE: 'Pharmacie', AUTRE: 'Autre' }[o] || o
    },
    fcfa(v) {
      return Number(v || 0).toLocaleString('fr-FR') + ' FCFA'
    },
    formatDate(d) {
      return d ? dayjs(d).format('DD/MM/YYYY') : ''
    },
    badge(s) {
      return {
        A_ENVOYER: 'bg-primary', ENVOYEE: 'bg-warning text-dark', PARTIELLEMENT_REGLEE: 'bg-info text-dark',
        REGLEE: 'bg-success', REJETEE: 'bg-danger', ANNULEE: 'bg-secondary'
      }[s] || 'bg-light text-dark'
    },
    badgeBordereau(s) {
      return { BROUILLON: 'bg-secondary', ENVOYE: 'bg-warning text-dark', PARTIELLEMENT_REGLE: 'bg-info text-dark', REGLE: 'bg-success' }[s]
    }
  }
}
</script>

<style scoped>
.header-clinique { padding: 15px 20px; background-color: #e9f5ff; border-radius: 12px; }
.card-clinique { background: #f8fafd; border-radius: 12px; border: none; }
.modern-table-clinique thead { background: linear-gradient(90deg, #8fd3f4, #0d6efd); color: #fff; }
.modal-backdrop-custom { position: fixed; inset: 0; background: rgba(0, 0, 0, .45); z-index: 1050; display: flex; align-items: flex-start; justify-content: center; padding: 60px 16px; }
.modal-card { width: 100%; max-width: 560px; border-radius: 12px; }
</style>
